import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Button, Popconfirm, Input, Tooltip, Drawer, Upload, Skeleton } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  FolderOpenOutlined,
  UploadOutlined,
  ReloadOutlined,
  MailOutlined,
  PhoneOutlined,
  TeamOutlined,
  InfoCircleOutlined,
  CloseCircleOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import MainLayout from '../components/MainLayout';
import EmployeeForm from '../components/EmployeeForm';
import { employeeService, Employee, EmployeeDocument } from '../services/employeeService';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { PageHeader, SectionCard, EmptyState, FilterChips, PersonAvatar, Pill } from '../components/ui';
import { fileKind } from '../components/ui/fileKind';
import './EmployeesPage.css';

type StatusFilter = 'active' | 'inactive';

/** What the backend accepts (EmployeeController::uploadDocument) and the mobile app offers. */
const ACCEPTED_DOCUMENTS = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx';

const EmployeesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [active, setActive] = useState<Employee[]>([]);
  const [inactive, setInactive] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [search, setSearch] = useState('');
  const [formVisible, setFormVisible] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | undefined>();
  const [regeneratingPasswordId, setRegeneratingPasswordId] = useState<number | null>(null);

  // Documents drawer
  const [documentsFor, setDocumentsFor] = useState<Employee | null>(null);
  const [documents, setDocuments] = useState<EmployeeDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentName, setDocumentName] = useState('');
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState<number | null>(null);

  // Both lists at once: the tabs can show counts, and switching needs no new request
  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const [activeList, inactiveList] = await Promise.all([
        employeeService.getAll(),
        employeeService.getInactive(),
      ]);
      setActive(activeList);
      setInactive(inactiveList);
    } catch (error) {
      toast.error(t('employees.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleAdd = async (values: Omit<Employee, 'id'>) => {
    try {
      await employeeService.create(values);
      toast.success(t('employees.createSuccess'));
      fetchEmployees();
    } catch (error: any) {
      // Field errors are shown on the form itself; anything else becomes a toast there
      if (!error.response?.data?.errors) {
        toast.error(error.response?.data?.message || t('employees.createError'));
      }
      throw error;
    }
  };

  const handleEdit = async (values: Omit<Employee, 'id'>) => {
    if (!selectedEmployee?.id) return;
    try {
      await employeeService.update(selectedEmployee.id, values);
      toast.success(t('employees.updateSuccess'));
      fetchEmployees();
    } catch (error: any) {
      if (!error.response?.data?.errors) {
        toast.error(error.response?.data?.message || t('employees.updateError'));
      }
      throw error;
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await employeeService.delete(id);
      toast.success(t('employees.deleteSuccess'));
      fetchEmployees();
    } catch (error) {
      toast.error(t('employees.deleteError'));
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await employeeService.activate(id);
      toast.success(t('employees.activateSuccess'));
      fetchEmployees();
    } catch (error) {
      toast.error(t('employees.activateError'));
    }
  };

  const handleRegeneratePassword = async (employeeId: number) => {
    try {
      setRegeneratingPasswordId(employeeId);
      await employeeService.regeneratePassword(employeeId);
      toast.success(t('employees.passwordRegenerated'));
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('employees.passwordRegenerationError'));
    } finally {
      setRegeneratingPasswordId(null);
    }
  };

  // ---------- Documents ----------
  const openDocuments = async (employee: Employee) => {
    setDocumentsFor(employee);
    setDocuments([]);
    setSelectedFile(null);
    setDocumentName('');
    try {
      setDocumentsLoading(true);
      setDocuments(await employeeService.getDocuments(employee.id!));
    } catch (error) {
      toast.error(t('employees.documents.fetchError'));
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleDocumentUpload = async () => {
    if (!documentsFor || !selectedFile || !documentName.trim()) return;
    try {
      setUploadingDocument(true);
      await employeeService.uploadDocument(documentsFor.id!, selectedFile, documentName.trim());
      toast.success(t('employees.documents.uploadSuccess'));
      setDocuments(await employeeService.getDocuments(documentsFor.id!));
      setSelectedFile(null);
      setDocumentName('');
    } catch (error: any) {
      if (error.response?.data?.errors) {
        Object.values(error.response.data.errors as Record<string, string[]>).forEach((messages) =>
          toast.error(messages[0]),
        );
      } else {
        toast.error(error.response?.data?.message || t('employees.documents.uploadError'));
      }
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    try {
      setDeletingDocumentId(documentId);
      await employeeService.deleteDocument(documentId);
      toast.success(t('employees.documents.deleteSuccess'));
      setDocuments((current) => current.filter((document) => document.id !== documentId));
    } catch (error) {
      toast.error(t('employees.documents.deleteError'));
    } finally {
      setDeletingDocumentId(null);
    }
  };

  // ---------- List ----------
  const normalizedSearch = search.trim().toLowerCase();
  const rows = useMemo(() => {
    const list = statusFilter === 'active' ? active : inactive;
    return list.filter(
      (employee) =>
        !normalizedSearch ||
        `${employee.name} ${employee.surname} ${employee.email} ${employee.phone ?? ''}`
          .toLowerCase()
          .includes(normalizedSearch),
    );
  }, [active, inactive, statusFilter, normalizedSearch]);

  const columns: ColumnsType<Employee> = [
    {
      title: t('employees.name'),
      key: 'employee',
      sorter: (a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`),
      render: (_, record) => (
        <div className="emp-cell">
          <PersonAvatar
            name={record.name}
            surname={record.surname}
            imageUrl={record.thumbnailUrl || record.imageUrl}
            size={38}
            muted={record.status === 'inactive'}
          />
          <div className="e22-row-body">
            <div className="e22-row-title">
              {record.name} {record.surname}
            </div>
            {record.address ? <div className="e22-row-subtitle">{record.address}</div> : null}
          </div>
        </div>
      ),
    },
    {
      title: t('employees.email'),
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
      render: (email: string) => (
        <a className="emp-contact" href={`mailto:${email}`}>
          <MailOutlined /> {email}
        </a>
      ),
    },
    {
      title: t('employees.phone'),
      dataIndex: 'phone',
      key: 'phone',
      width: 190,
      sorter: (a, b) => (a.phone || '').localeCompare(b.phone || ''),
      render: (phone?: string) =>
        phone ? (
          <a className="emp-contact" href={`tel:${phone}`}>
            <PhoneOutlined /> {phone}
          </a>
        ) : (
          <span className="emp-muted">—</span>
        ),
    },
    {
      title: t('employees.status'),
      key: 'status',
      width: 120,
      render: (_, record) =>
        record.status === 'inactive' ? (
          <Pill>{t('employees.inactive')}</Pill>
        ) : (
          <Pill tone="success">{t('employees.active')}</Pill>
        ),
    },
    {
      title: t('employees.actions'),
      key: 'actions',
      width: 190,
      align: 'right',
      render: (_, record) =>
        record.status === 'active' ? (
          <div className="emp-actions">
            <Tooltip title={t('employees.viewDocuments')}>
              <Button type="text" icon={<FolderOpenOutlined />} onClick={() => openDocuments(record)} />
            </Tooltip>
            <Tooltip title={t('employees.editEmployee')}>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => {
                  setSelectedEmployee(record);
                  setFormVisible(true);
                }}
              />
            </Tooltip>
            <Popconfirm
              title={t('employees.regeneratePasswordConfirm')}
              description={t('employees.regeneratePasswordDescription')}
              onConfirm={() => handleRegeneratePassword(record.id!)}
              okText={t('common.yes')}
              cancelText={t('common.no')}
            >
              <Tooltip title={t('employees.regeneratePassword')}>
                <Button type="text" icon={<ReloadOutlined />} loading={regeneratingPasswordId === record.id} />
              </Tooltip>
            </Popconfirm>
            <Popconfirm
              title={t('employees.deleteConfirm')}
              onConfirm={() => handleDelete(record.id!)}
              okText={t('common.yes')}
              cancelText={t('common.no')}
              okButtonProps={{ danger: true }}
            >
              <Tooltip title={t('employees.deleteEmployee')}>
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          </div>
        ) : (
          <div className="emp-actions">
            <Button icon={<CheckCircleOutlined />} onClick={() => handleActivate(record.id!)}>
              {t('employees.activateEmployee')}
            </Button>
          </div>
        ),
    },
  ];

  return (
    <MainLayout bare>
      <PageHeader
        title={t('employees.title')}
        subtitle={loading ? undefined : t('employees.summary', { active: active.length, inactive: inactive.length })}
        actions={
          <>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: 'var(--e22-text-muted)' }} />}
              placeholder={t('employees.searchPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={{ width: 280 }}
            />
            <Button icon={<ReloadOutlined spin={loading} />} onClick={fetchEmployees} disabled={loading}>
              {t('dashboard.refresh')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setSelectedEmployee(undefined);
                setFormVisible(true);
              }}
            >
              {t('employees.addEmployee')}
            </Button>
          </>
        }
      />

      <SectionCard
        title={statusFilter === 'active' ? t('employees.active') : t('employees.inactive')}
        icon={<TeamOutlined />}
        subtitle={loading ? undefined : t('employees.showingCount', { count: rows.length })}
        action={
          <FilterChips<StatusFilter>
            ariaLabel={t('employees.status')}
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { key: 'active', label: t('employees.active'), count: active.length },
              { key: 'inactive', label: t('employees.inactive'), count: inactive.length },
            ]}
          />
        }
      >
        {loading ? (
          <Skeleton active avatar paragraph={{ rows: 6 }} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={normalizedSearch ? <SearchOutlined /> : <TeamOutlined />}
            text={
              normalizedSearch
                ? t('employees.noMatching')
                : statusFilter === 'active'
                  ? t('employees.noActive')
                  : t('employees.noInactive')
            }
            action={
              normalizedSearch ? (
                <button type="button" className="e22-link-button" onClick={() => setSearch('')}>
                  {t('employees.clearSearch')}
                </button>
              ) : undefined
            }
          />
        ) : (
          <Table
            className="emp-table"
            dataSource={rows}
            columns={columns}
            pagination={rows.length > 15 ? { pageSize: 15, showSizeChanger: false } : false}
            rowKey="id"
            scroll={{ x: 900 }}
            size="middle"
          />
        )}
      </SectionCard>

      <EmployeeForm
        visible={formVisible}
        onClose={() => {
          setFormVisible(false);
          setSelectedEmployee(undefined);
        }}
        onSubmit={selectedEmployee ? handleEdit : handleAdd}
        initialValues={selectedEmployee}
        title={selectedEmployee ? t('employees.editEmployee') : t('employees.addEmployee')}
        hint={selectedEmployee ? undefined : t('employees.defaultPasswordHint')}
      />

      {/* Documents */}
      <Drawer
        title={t('employees.documents.title')}
        placement="right"
        onClose={() => setDocumentsFor(null)}
        open={!!documentsFor}
        width={560}
      >
        {documentsFor && (
          <>
            <div className="emp-doc-owner">
              <PersonAvatar
                name={documentsFor.name}
                surname={documentsFor.surname}
                imageUrl={documentsFor.thumbnailUrl || documentsFor.imageUrl}
                size={40}
              />
              <div className="e22-row-body">
                <div className="e22-row-title">
                  {documentsFor.name} {documentsFor.surname}
                </div>
                <div className="e22-row-subtitle">{documentsFor.email}</div>
              </div>
            </div>

            {/* Upload */}
            <div className="emp-upload">
              {selectedFile ? (
                <>
                  <div className="emp-picked">
                    <span
                      className="emp-file-icon"
                      style={{ background: fileKind(selectedFile.name).soft, color: fileKind(selectedFile.name).color }}
                    >
                      {fileKind(selectedFile.name).icon}
                    </span>
                    <div className="e22-row-body">
                      <div className="e22-row-title">{selectedFile.name}</div>
                      <div className="e22-row-subtitle">
                        {fileKind(selectedFile.name).label} · {Math.max(1, Math.round(selectedFile.size / 1024))} KB
                      </div>
                    </div>
                    <Button
                      type="text"
                      icon={<CloseCircleOutlined />}
                      onClick={() => {
                        setSelectedFile(null);
                        setDocumentName('');
                      }}
                      disabled={uploadingDocument}
                      aria-label={t('common.delete')}
                    />
                  </div>
                  <Input
                    placeholder={t('employees.documents.namePlaceholder')}
                    value={documentName}
                    onChange={(event) => setDocumentName(event.target.value)}
                    maxLength={255}
                    disabled={uploadingDocument}
                    onPressEnter={handleDocumentUpload}
                  />
                  <Button
                    type="primary"
                    block
                    loading={uploadingDocument}
                    disabled={!documentName.trim()}
                    onClick={handleDocumentUpload}
                  >
                    {t('employees.documents.uploadDocument')}
                  </Button>
                </>
              ) : (
                <Upload.Dragger
                  accept={ACCEPTED_DOCUMENTS}
                  showUploadList={false}
                  multiple={false}
                  beforeUpload={(file) => {
                    setSelectedFile(file);
                    setDocumentName(file.name.replace(/\.[^.]+$/, '').slice(0, 255));
                    return false;
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                  </p>
                  <p className="ant-upload-text">{t('employees.documents.chooseDocument')}</p>
                  <p className="ant-upload-hint">{t('employees.documents.allowedTypes')}</p>
                </Upload.Dragger>
              )}
            </div>

            {/* Stored documents */}
            <div className="emp-doc-list-head">
              <span className="e22-section-title">{t('employees.documents.title')}</span>
              <span className="emp-muted">{t('documents.count', { count: documents.length })}</span>
            </div>

            {documentsLoading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : documents.length === 0 ? (
              <EmptyState icon={<FolderOpenOutlined />} text={t('employees.documents.noDocuments')} />
            ) : (
              documents.map((document) => {
                const kind = fileKind(document.path || document.document_url);
                return (
                  <div key={document.id} className="e22-row">
                    <span className="emp-file-icon" style={{ background: kind.soft, color: kind.color }}>
                      {kind.icon}
                    </span>
                    <div className="e22-row-body">
                      <div className="e22-row-title">{document.name}</div>
                      <div className="e22-row-subtitle">
                        {kind.label} · {t('employees.documents.uploaded')}:{' '}
                        {new Date(document.created_at).toLocaleDateString(i18n.language, {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </div>
                    </div>
                    <Tooltip title={t('employees.documents.viewDocument')}>
                      <Button
                        type="text"
                        icon={<ExportOutlined />}
                        href={document.document_url}
                        target="_blank"
                        rel="noreferrer"
                      />
                    </Tooltip>
                    <Popconfirm
                      title={t('employees.documents.deleteConfirm')}
                      description={document.name}
                      onConfirm={() => handleDeleteDocument(document.id)}
                      okText={t('common.yes')}
                      cancelText={t('common.no')}
                      okButtonProps={{ danger: true }}
                    >
                      <Tooltip title={t('employees.documents.deleteDocument')}>
                        <Button type="text" danger icon={<DeleteOutlined />} loading={deletingDocumentId === document.id} />
                      </Tooltip>
                    </Popconfirm>
                  </div>
                );
              })
            )}

            <div className="emp-doc-note">
              <InfoCircleOutlined />
              <span>{t('employees.documents.visibleHint')}</span>
            </div>
          </>
        )}
      </Drawer>
    </MainLayout>
  );
};

export default EmployeesPage;
